import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import {
  emailPatternRule,
  maxLengthRule,
  postalCodePatternRule,
  PREVIOUSLY_ENTERED,
  requiredText,
  taxIdPatternRule,
  trimString,
} from '#/Scripts/shared/form/validation';
import { YES_NO_OPTIONS } from '#/Scripts/shared/signupOptions';
import { getStateProvinceOptions } from '#/Scripts/shared/stateOptions';
import { isValidPhoneNumber } from '#/Scripts/utilities/PhoneNumber';
import Button from '#sign-up/components/common/Button/Button';
import { ButtonVariants } from '#sign-up/components/common/Button/buttonTypes';
import { IconButton } from '#sign-up/components/common/Button/IconButton';
import FormTextField from '#sign-up/components/common/FormTextField/FormTextField';
import Select from '#sign-up/components/common/Select/Select';
import { Modal } from '#sign-up/components/Modal/Modal';
import {
  Country,
  CountryDisplayNames,
  enumToDropdownOptions,
  SignupFormValues,
} from '#sign-up/components/SignUp/types';

export const ControllerInformationForm = () => {
  const form = useFormContext<SignupFormValues>();
  const { control, register, formState } = form;
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'Controllers',
  });

  const companyCountryId = form.watch('CountryId');
  const watchedControllers = form.watch('Controllers');
  const isPublicEntity = form.watch('IsPublicEntity');

  const [touchedControllers, setTouchedControllers] = useState<Record<number, boolean>>({});
  const [isOpen, setIsOpen] = useState(false);
  const isFirstLoad = useRef(true);
  const ssnRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const isCompanyCanada = companyCountryId === Country.Canada;
  const countryOptions = enumToDropdownOptions(Country, CountryDisplayNames);

  const toggleModal = () => setIsOpen((prev) => !prev);

  const handleReadOnlyClick = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
    e.preventDefault();
    (e.target as HTMLInputElement).blur();
  }, []);

  const handleReadOnlyFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.target.blur();
  }, []);

  const handleEditSSN = useCallback(
    (idx: number) => () => {
      form.setValue(`Controllers.${idx}.SocialSecurityNumber`, '');
      ssnRefs.current[idx]?.focus();
    },
    [form]
  );

  const handleSSNBlur = useCallback(
    (idx: number) => (e: React.FocusEvent<HTMLInputElement>) => {
      const value = e.target.value.trim();
      form.setValue(`Controllers.${idx}.SocialSecurityNumber`, value, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [form]
  );

  const renderEditIcon = useCallback(
    (isReadonly: boolean, onClick: () => void, testId: string) =>
      isReadonly ? (
        <IconButton
          className="edit-icon"
          iconName="edit"
          title="Edit"
          onClick={onClick}
          variant={ButtonVariants.outline}
          data-testid={testId}
          style={{ border: 'none', boxShadow: 'none' }}
        />
      ) : undefined,
    []
  );

  // Helper to check if a controller has any data filled in
  const isControllerTouched = useCallback((controller: SignupFormValues['Controllers'][0]) => {
    return !!(
      controller.FirstName ||
      controller.LastName ||
      controller.DateOfBirth ||
      controller.JobTitle ||
      controller.IsOwner !== undefined ||
      controller.SocialSecurityNumber ||
      controller.EmailAddress ||
      controller.PhoneNumber ||
      controller.StreetAddress ||
      controller.Suite ||
      controller.City ||
      controller.State ||
      controller.PostalCode
    );
  }, []);

  // 1. On first render, set default controller with company country
  useEffect(() => {
    if (isFirstLoad.current && companyCountryId) {
      if (!watchedControllers || watchedControllers.length === 0) {
        form.setValue('Controllers', [
          {
            FirstName: '',
            LastName: '',
            DateOfBirth: '',
            JobTitle: '',
            IsOwner: undefined,
            SocialSecurityNumber: '',
            EmailAddress: '',
            PhoneNumber: '',
            StreetAddress: '',
            Suite: '',
            City: '',
            State: '',
            CountryId: companyCountryId,
            Country: '',
            PostalCode: '',
            Key: '',
          },
        ]);
      }
      isFirstLoad.current = false;
    }
  }, [companyCountryId]);

  // 2. When companyCountryId changes, update all untouched controllers (new instance only)
  useEffect(() => {
    if (!isFirstLoad.current && watchedControllers?.length > 0) {
      watchedControllers.forEach((controller, idx) => {
        const hasBeenTouched = touchedControllers[idx] || isControllerTouched(controller);
        if (controller.CountryId !== companyCountryId && !hasBeenTouched) {
          update(idx, { ...controller, CountryId: companyCountryId });
        }
      });
    }
  }, [companyCountryId, touchedControllers]);

  // Handler for country change with touch tracking
  const handleCountryChange = useCallback(
    (idx: number, fieldOnChange: (value: number | null) => void) => (e: React.ChangeEvent<HTMLSelectElement>) => {
      setTouchedControllers((prev) => ({ ...prev, [idx]: true }));
      const value = e.target.value;
      fieldOnChange(value ? Number.parseInt(value, 10) : null);
      // Reset State when Country changes
      form.setValue(`Controllers.${idx}.State`, '', { shouldValidate: false });
    },
    []
  );

  // Set up refs for SSN fields
  useEffect(() => {
    fields.forEach((_, idx) => {
      ssnRefs.current[idx] ??= document.querySelector<HTMLInputElement>(
        `input[name="Controllers.${idx}.SocialSecurityNumber"]`
      );
    });
  }, [fields]);

  // Clear SSN values when company becomes public entity
  useEffect(() => {
    if (isPublicEntity && watchedControllers?.length > 0) {
      watchedControllers.forEach((controller, idx) => {
        if (controller.SocialSecurityNumber && controller.SocialSecurityNumber !== '') {
          form.setValue(`Controllers.${idx}.SocialSecurityNumber`, '', {
            shouldValidate: false,
            shouldDirty: true,
          });
        }
      });
    }
  }, [isPublicEntity, watchedControllers, form]);

  return (
    <div className="signup-form controller-information">
      <div className="signup-form-header">
        <h1>Controller Information</h1>
        <ul>
          <li>
            This information is required for all owners with 25% or greater ownership. If no individual has greater than
            25% ownership, we must have this information for the control owner, officer or executive of the company.
          </li>
          <li>
            The {isCompanyCanada ? 'social insurance number (SIN)' : 'social security number (SSN)'} or employer
            identification number (EIN) must also be collected unless the organization is publicly traded on a US
            recognized exchange, a US government entity, or a Canadian entity.
          </li>
          <li>All foreign controllers will be required to submit government issued photo identification.</li>
          <li>
            If the organization is a non-profit (501c3), we will need to collect the information below for the person
            with significant responsibility to control the customer.
          </li>
        </ul>
      </div>

      <div className="account-conditions">
        <Button onClick={toggleModal} variant={ButtonVariants.link}>
          Why does ePayPolicy request personal information?
        </Button>
      </div>

      <Modal
        isOpen={isOpen}
        handleClose={toggleModal}
        title="Why does ePayPolicy request personal information?"
        footer={<Button onClick={toggleModal}>Close</Button>}
      >
        <div>
          <p>
            To help fight the funding of terrorism and money-laundering activities, we are required to obtain, verify
            and record information that identifies persons who engage in certain transactions with or through our
            company. This means that we will verify your name, residential or street address, date of birth and{' '}
            {isCompanyCanada ? 'SIN' : 'SSN'}/EIN or other tax identification number on the application. We may also ask
            to see a driver’s license or other identifying documents from you. We are also obligated in many instances
            to obtain information regarding your direct and indirect owners.
          </p>
          <br />
          <p>
            This information is not used for personal guarantees or credit checks. The personal information is primarily
            used for two checks; one is the Office of Foreign Assets Control (OFAC) which maintains a list of those
            involved in offshore money laundering and terrorist acts and the other check is against the Mastercard MATCH
            list of individuals that have previously accepted credit cards and committed fraud against the card brands
            (Visa, Mastercard, etc).
          </p>
        </div>
      </Modal>

      {fields.map((field, idx) => {
        const isCanada = watchedControllers?.[idx]?.CountryId === Country.Canada;
        const ssn = watchedControllers?.[idx]?.SocialSecurityNumber;

        return (
          <div key={field.id} className={`controller-section ${idx === 0 ? '' : 'with-border'}`}>
            {fields.length > 1 && idx !== 0 && (
              <div className="remove-button-container">
                <Button
                  type="button"
                  variant={ButtonVariants.plain}
                  icon="remove_circle_outline"
                  className="remove-button"
                  onClick={() => remove(idx)}
                >
                  Remove controller
                </Button>
              </div>
            )}

            <div className="signup-form-row">
              <FormTextField
                label="First Name"
                {...register(`Controllers.${idx}.FirstName`, {
                  ...requiredText('First Name'),
                  ...maxLengthRule(40, 'First Name'),
                  ...trimString,
                })}
                errors={formState.errors.Controllers?.[idx]?.FirstName}
                className="with-subtext"
                autoComplete="given-name"
              />
              <FormTextField
                label="Last Name"
                {...register(`Controllers.${idx}.LastName`, {
                  ...requiredText('Last Name'),
                  ...maxLengthRule(40, 'Last Name'),
                  ...trimString,
                })}
                errors={formState.errors.Controllers?.[idx]?.LastName}
                className="with-subtext"
                autoComplete="family-name"
              />
            </div>

            <div className="signup-form-row">
              <FormTextField
                label="Job Title"
                {...register(`Controllers.${idx}.JobTitle`, {
                  ...requiredText('Job Title'),
                  ...maxLengthRule(40, 'Job Title'),
                  ...trimString,
                })}
                errors={formState.errors.Controllers?.[idx]?.JobTitle}
                className="with-subtext"
              />
              <Controller
                name={`Controllers.${idx}.IsOwner`}
                control={control}
                rules={{
                  validate: (val) => val !== undefined || 'Please select if this Controller owns 25% or more.',
                }}
                render={({ field }) => {
                  let selectedValue = '';

                  if (field.value === true) {
                    selectedValue = 'true';
                  } else if (field.value === false) {
                    selectedValue = 'false';
                  }

                  return (
                    <Select
                      label="Does this Controller own 25% or more?"
                      placeholder="Select..."
                      options={YES_NO_OPTIONS}
                      value={selectedValue}
                      onChange={(e) => {
                        const value = e.target.value;

                        if (value === '') {
                          field.onChange(undefined);
                          return;
                        }

                        field.onChange(value === 'true');
                      }}
                      onBlur={field.onBlur}
                      name={field.name}
                      className="with-subtext"
                      errors={formState.errors}
                    />
                  );
                }}
              />
            </div>

            <div className="signup-form-row">
              <Controller
                name={`Controllers.${idx}.CountryId`}
                control={control}
                rules={{
                  required: 'Country is required.',
                }}
                render={({ field }) => (
                  <Select
                    label="Country"
                    placeholder="Select..."
                    options={countryOptions}
                    value={watchedControllers?.[idx]?.CountryId ?? ''}
                    onChange={handleCountryChange(idx, field.onChange)}
                    onBlur={field.onBlur}
                    name={field.name}
                    errors={formState.errors}
                    className="with-subtext"
                  />
                )}
              />
              {!isPublicEntity && (
                <FormTextField
                  label={isCanada ? 'Social Insurance Number' : 'Social Security Number'}
                  className="with-subtext"
                  placeholder={ssn === PREVIOUSLY_ENTERED ? 'Previously entered' : undefined}
                  readOnly={ssn === PREVIOUSLY_ENTERED}
                  onClick={ssn === PREVIOUSLY_ENTERED ? handleReadOnlyClick : undefined}
                  onFocus={ssn === PREVIOUSLY_ENTERED ? handleReadOnlyFocus : undefined}
                  icon={renderEditIcon(ssn === PREVIOUSLY_ENTERED, handleEditSSN(idx), `edit-ssn-${idx}`)}
                  {...register(`Controllers.${idx}.SocialSecurityNumber`, {
                    validate: (value) => {
                      // Allow previously entered sentinel (value not changed)
                      if (value === PREVIOUSLY_ENTERED) return true;

                      // Not required if public entity
                      if (isPublicEntity) return true;

                      if (!value || value.trim().length === 0) {
                        return isCanada
                          ? 'Social Insurance Number is required.'
                          : 'Social Security Number is required.';
                      }

                      // Apply pattern validation
                      const patternRule = taxIdPatternRule(
                        isCanada ? 'Social Insurance Number' : 'Social Security Number',
                        isCanada
                      );
                      const pattern = patternRule.pattern as { value: RegExp; message: string };
                      return pattern.value.test(value) || pattern.message;
                    },
                    ...trimString,
                    onBlur: handleSSNBlur(idx),
                  })}
                  errors={formState.errors.Controllers?.[idx]?.SocialSecurityNumber}
                />
              )}
              <FormTextField
                label="Date of Birth"
                type="date"
                {...register(`Controllers.${idx}.DateOfBirth`, {
                  required: 'Date of Birth is required.',
                  validate: {
                    isValidDate: (value) => {
                      const date = new Date(value);

                      if (Number.isNaN(date.getTime())) {
                        return 'Invalid date. Please select a valid date.';
                      }

                      const lowerBound = new Date('1900-01-01');
                      if (date < lowerBound) {
                        return 'Date of Birth cannot be before 01/01/1900.';
                      }

                      const today = new Date();
                      const age = today.getFullYear() - date.getFullYear();
                      const monthDiff = today.getMonth() - date.getMonth();
                      const dayDiff = today.getDate() - date.getDate();

                      if (age < 18 || (age === 18 && (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)))) {
                        return 'You must be at least 18 years old.';
                      }

                      return true;
                    },
                  },
                })}
                errors={formState.errors.Controllers?.[idx]?.DateOfBirth}
                className="with-subtext"
                autoComplete="bday"
              />
            </div>

            <div className="signup-form-row">
              <FormTextField
                label="Email Address"
                {...register(`Controllers.${idx}.EmailAddress`, {
                  ...emailPatternRule('Email Address'),
                  ...trimString,
                })}
                errors={formState.errors.Controllers?.[idx]?.EmailAddress}
                className="with-subtext"
              />
              <FormTextField
                label="Phone Number"
                {...register(`Controllers.${idx}.PhoneNumber`, {
                  ...requiredText('Phone Number'),
                  validate: {
                    isValidPhoneNumber: (value) => (value && isValidPhoneNumber(value)) || 'Phone Number is invalid.',
                  },
                  ...trimString,
                })}
                errors={formState.errors.Controllers?.[idx]?.PhoneNumber}
                className="with-subtext"
              />
            </div>

            <div className="signup-form-row">
              <FormTextField
                label="Address 1"
                {...register(`Controllers.${idx}.StreetAddress`, {
                  ...requiredText('Address 1'),
                  ...maxLengthRule(100, 'Address 1'),
                  ...trimString,
                })}
                errors={formState.errors.Controllers?.[idx]?.StreetAddress}
                className="with-subtext"
                autoComplete="address-line1"
              />
              <FormTextField
                label="Address 2"
                {...register(`Controllers.${idx}.Suite`, {
                  ...maxLengthRule(50, 'Address 2'),
                  ...trimString,
                })}
                errors={formState.errors.Controllers?.[idx]?.Suite}
                className="with-subtext"
                autoComplete="address-line2"
              />
            </div>

            <div className="signup-form-row">
              <FormTextField
                label="City"
                {...register(`Controllers.${idx}.City`, {
                  ...requiredText('City'),
                  ...maxLengthRule(100, 'City'),
                  ...trimString,
                })}
                errors={formState.errors.Controllers?.[idx]?.City}
                className="with-subtext"
                autoComplete="address-level2"
              />
              <div className="address-fields">
                <div className="field narrow">
                  <Select
                    label={isCanada ? 'Province' : 'State'}
                    placeholder="Select..."
                    options={getStateProvinceOptions(isCanada).map((x) => ({ Text: x, Value: x }))}
                    {...register(`Controllers.${idx}.State`, {
                      required: `${isCanada ? 'Province' : 'State'} is required.`,
                      ...trimString,
                    })}
                    errors={formState.errors}
                    className="with-subtext"
                  />
                </div>
                <div className="field narrow">
                  <FormTextField
                    label="Postal Code"
                    {...register(`Controllers.${idx}.PostalCode`, {
                      ...requiredText('Postal Code'),
                      ...postalCodePatternRule(isCanada),
                      ...trimString,
                    })}
                    errors={formState.errors.Controllers?.[idx]?.PostalCode}
                    className="with-subtext"
                    autoComplete="postal-code"
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <div className="add-controller-button">
        <Button
          icon="add_circle_outline"
          variant={ButtonVariants.plain}
          type="button"
          onClick={() =>
            append({
              FirstName: '',
              LastName: '',
              DateOfBirth: '',
              JobTitle: '',
              IsOwner: undefined,
              SocialSecurityNumber: '',
              EmailAddress: '',
              PhoneNumber: '',
              StreetAddress: '',
              Suite: '',
              City: '',
              State: '',
              CountryId: companyCountryId,
              Country: '',
              PostalCode: '',
              Key: '',
            })
          }
        >
          Add another controller
        </Button>
      </div>

      <div />
    </div>
  );
};
